import Button from "../../components/styled/Button";
import {
  FormHeadline,
  Input,
  FormParagraph,
} from "../../components/styled/Form";
import WalletForm from "../../components/WalletForm";
import { TxConfirmContainer, WrappedRow } from "./styled";
import InfoBlock from "./InfoBlock";
import { useCallback, useState, useEffect } from "react";
import { useWeb3React } from "@web3-react/core";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch, RootState } from "../../store";
import { Row } from "../../components/styled/Flex";
import { Ripple } from "../../components/Loaders/Ripple";
import { useNavigate } from "react-router-dom";
import FormSteps from "../../components/FormSteps";
import { Divider } from "../../components/styled/Divider";
import { ErrorText, Paragraph } from "../../components/styled/Text";

const TransactionConfirm = () => {
  const navigator = useNavigate();
  const { library, account, chainId } = useWeb3React();
  const [erc20Token, setErc20Token] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [isCheckingTokenInfo, setIsCheckingTokenInfo] =
    useState<boolean>(false);
  const [transactionTokenCost, setTransactionTokenCost] = useState<string>("");
  const [allowAirdrop, setAllowAirdrop] = useState<boolean>(false);
  const dispatch = useDispatch<Dispatch>();
  const tokenInfo = useSelector((state: RootState) => state.disperse.tokenInfo);
  const isSpendApproved = useSelector(
    (state: RootState) => state.disperse.isSpendApproved
  );

  const isTokenLSS = useSelector(
    (state: RootState) => state.disperse.tokenInfo.isLossless
  );

  const snapshotData = useSelector(
    (state: RootState) => state.snapshotModel.snapshotData
  );

  const handleErc20Info = useCallback(async () => {
    if (library && erc20Token && account && chainId && snapshotData) {
      try {
        setIsCheckingTokenInfo(true);
        await dispatch.disperse.initContracts({
          tokenAddress: erc20Token,
          library: library,
          account: account,
          chainId: chainId,
        });

        await dispatch.disperse.fetchTokenInfo({
          account: account,
          library: library,
        });

        await dispatch.disperse.checkAllowance({
          account: account,
          chainId: chainId,
        });

        const txTokenCost = snapshotData.reduce(
          (acc, curr) => acc + Number(curr.balance),
          0
        );
        setTransactionTokenCost(txTokenCost.toString());

        setIsCheckingTokenInfo(false);
        setError(false);
      } catch (e) {
        setError(true);
        setIsCheckingTokenInfo(false);
      }
    }
  }, [erc20Token, library, account, dispatch, chainId, setError, snapshotData]);

  useEffect(() => {
    dispatch.disperse.resetTokenInfo();
    setError(false);
  }, [chainId, account, dispatch]);

  useEffect(() => {
    if (!tokenInfo) return;

    if (
      tokenInfo &&
      tokenInfo.balance &&
      parseInt(tokenInfo.balance) < parseInt(transactionTokenCost)
    ) {
      setAllowAirdrop(false);
    } else {
      setAllowAirdrop(true);
    }
  }, [snapshotData, tokenInfo, transactionTokenCost]);

  const doAirdrop = useCallback(async () => {
    if (library && erc20Token && account && chainId) {
      try {
        if (isSpendApproved && tokenInfo) {
          await dispatch.disperse.prepareAirdrop({
            account: account,
            library: library,
          });

          dispatch.disperse.commitAirdrop({
            account: account,
          });
          navigator("/transactions");
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [
    erc20Token,
    library,
    account,
    dispatch,
    chainId,
    tokenInfo,
    isSpendApproved,
    navigator,
  ]);

  const handleRelaunch = async () => {
    if (chainId && account && library) {
      try {
        setIsApproving(true);
        const txReceipt = await dispatch.disperse.approveSpend({
          account: account,
          chainId: chainId,
        });
        if (txReceipt) {
          await dispatch.disperse.checkAllowance({
            account: account,
            chainId: chainId,
          });
        }
        setIsApproving(false);
      } catch (e) {
        console.error(e);
        setIsApproving(false);
      }
    }
  };

  const handleBack = () => {
    navigator("/snapshot-review");
  };

  useEffect(() => {
    dispatch.multistepModel.setCurrentStep(4);
    return () => {
      dispatch.multistepModel.setCurrentStep(3);
    };
  }, [dispatch]);

  return (
    <TxConfirmContainer>
      <InfoBlock />
      <WalletForm marginTop="50px" marginBottom="50px">
        <FormSteps />
        <Divider />
        <FormHeadline>Token Information</FormHeadline>
        <FormParagraph marginBottom="15px">
          Please indicate your new LSS protected token address.
        </FormParagraph>
        {isCheckingTokenInfo ? (
          <WrappedRow justify="center">
            <Ripple />
          </WrappedRow>
        ) : (
          <WrappedRow>
            <Input
              placeholder="Enter your new token Address"
              onChange={(e) => setErc20Token(e.target.value)}
            ></Input>
            <Button onClick={handleErc20Info}>Load token</Button>
          </WrappedRow>
        )}

        {
          // if tokenName exists and is not checking token info
          tokenInfo.name && !isCheckingTokenInfo ? (
            <>
              <Row
                direction="column"
                alignItems="start"
                style={{
                  marginTop: "25px",
                }}
              >
                <Paragraph>
                  <strong>Token name:</strong> {tokenInfo.name}
                </Paragraph>
                <Paragraph>
                  <strong>Balance:</strong> {tokenInfo.balance}{" "}
                  {tokenInfo.symbol}
                </Paragraph>
                <Paragraph>
                  <strong>Symbol:</strong> {tokenInfo.symbol}
                </Paragraph>
                <Paragraph>
                  <strong>
                    Airdrop Cost: {transactionTokenCost} {tokenInfo.symbol}
                  </strong>
                </Paragraph>
              </Row>
            </>
          ) : null
        }

        {error && (
          <WrappedRow
            justify="center"
            style={{
              marginTop: "25px",
            }}
          >
            <ErrorText>Token invalid, or was not found on this chain</ErrorText>
          </WrappedRow>
        )}

        {!allowAirdrop && (
          <WrappedRow
            justify="center"
            style={{
              marginTop: "25px",
            }}
          >
            <ErrorText>
              Not enough {tokenInfo.symbol} balance to do the Airdrop
            </ErrorText>
          </WrappedRow>
        )}

        {tokenInfo.name && !isTokenLSS && (
          <WrappedRow
            justify="center"
            style={{
              marginTop: "25px",
            }}
          >
            <ErrorText>Token is not Lossless Protected</ErrorText>
            <Paragraph textAlign="center">
              If you want to relaunch your token with this tool. Please contact
              us at:{" "}
              <strong>
                <a
                  href="mailto:
                  hello@lossless.cash"
                >
                  hello@lossless.cash
                </a>
              </strong>
            </Paragraph>
          </WrappedRow>
        )}

        <Row
          justify="space-between"
          style={{
            marginTop: "25px",
          }}
        >
          <Button onClick={handleBack}>Go back</Button>

          {tokenInfo.name && (
            <>
              {isApproving && <Ripple />}
              {allowAirdrop && isSpendApproved && !isApproving && (
                <Button onClick={doAirdrop}>Relaunch</Button>
              )}

              {!isSpendApproved && !isApproving && isTokenLSS && (
                <Button
                  onClick={handleRelaunch}
                  disabled={error || !isTokenLSS}
                >
                  {isSpendApproved ? "Relaunch" : "Approve"}
                </Button>
              )}
            </>
          )}
        </Row>
      </WalletForm>
    </TxConfirmContainer>
  );
};

export default TransactionConfirm;
